import React, {forwardRef} from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./dateRangeStylesReset.css";
import * as moment from "moment";
import DateTime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import PropTypes from "prop-types";

const ExampleCustomInput = forwardRef(({ value, onClick, className }, ref) => (
  <button type="button" className={className} onClick={onClick} ref={ref}>
    {value}
  </button>
));

ExampleCustomInput.propTypes = {
  value: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  className: PropTypes.string,
};

export class DatePickerComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      date: props.date ? new Date(props.date) : null,
    };

    const m = moment();
    this.lastSelectableDay = props.todaySelectable ? m : m.subtract(1, "day");
  }

  componentDidUpdate(prevProps) {
    if (prevProps.date !== this.props.date) {
      this.setState({ date: this.props.date ? new Date(this.props.date) : null });
    }
  }

  isSelectable = (date) => {
    // Pass a moment to isSameOrBefore for reliable comparison
    return moment(date).isSameOrBefore(this.lastSelectableDay, "day");
  };

  render() {
    const { withoutDefaultBorder } = this.props;
    const { date } = this.state;
    let safeDate = null;
    if (date instanceof Date) {
      safeDate = date;
    } else if (date) {
      safeDate = new Date(date);
    }


    return (
      <DatePicker
      showYearDropdown
      showMonthDropdown
      scrollableYearDropdown
        selected={safeDate}
        onChange={date => {
          this.setState({ date });
          if (this.props.onDateChange) this.props.onDateChange(date ? moment(date) : null);
        }}
        filterDate={this.isSelectable}
        dateFormat="d MMM yyyy"
        placeholderText="Select Date"
        className={withoutDefaultBorder ? "no-border" : ""}
        customInput={<ExampleCustomInput className="custom-input" />}
      />
    );
  }
}


DatePickerComponent.propTypes = {
  date: PropTypes.any,
  onDateChange: PropTypes.func,
  todaySelectable: PropTypes.bool,
  withoutDefaultBorder: PropTypes.bool,
};


const safeDate = (d) => {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  return isNaN(dt.getTime()) ? null : dt;
};

export class RangePicker extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      startDate: safeDate(props.startDate),
      endDate: safeDate(props.endDate),
      numberOfMonths: props.monthsToDisplay,
    };

    this.lastSelectableDay = moment().subtract(1, "day").toDate();
  }

  isSelectable = (date) => {
    const compareDate = date instanceof Date ? date : new Date(date);
    return compareDate <= this.lastSelectableDay;
  };

  render() {
    return (
      <DatePicker
      showYearDropdown
      showMonthDropdown
      scrollableYearDropdown
        selectsRange
        startDate={this.state.startDate}
        endDate={this.state.endDate}
        onChange={(dates) => {
          const [start, end] = dates;
          this.setState({
            startDate: safeDate(start),
            endDate: safeDate(end),
          });
          if (this.props.onDatesChange) {
            this.props.onDatesChange({
              startDate: start ? moment(start) : null,
              endDate: end ? moment(end) : null,
            });
          }
        }}
        filterDate={this.isSelectable}
        monthsShown={this.state.numberOfMonths}
        dateFormat="d MMM yyyy"
        placeholderText="Select date range"
        className="no-border w-100 text-center"

      />
    );
  }
}

RangePicker.propTypes = {
  startDate: PropTypes.any,
  endDate: PropTypes.any,
  monthsToDisplay: PropTypes.number,
  onDatesChange: PropTypes.func,
};
export class RangePickerforFrequency extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      startDate: props.startDate ? moment(props.startDate).toDate() : null,
      endDate: props.endDate ? moment(props.endDate).toDate() : null,
      numberOfMonths: props.monthsToDisplay ?? 1,
      minimumNights: props.minimumNights ?? 0,
    };

    this.lastSelectableDay = moment().subtract(7, "days").toDate();
  }

  isSelectable = (date) => {
    return date <= this.lastSelectableDay;
  };

  render() {
    return (
      <DatePicker
        selectsRange
        showYearDropdown
        showMonthDropdown
        scrollableYearDropdown
        monthsShown={this.state.numberOfMonths}
        startDate={this.state.startDate}
        endDate={this.state.endDate}
        onChange={(dates) => {
          const [start] = dates;
          const end = start ? moment(start).add(6, "days").toDate() : null;
          this.setState({ startDate: start, endDate: end });

          this.props.onDatesChange({
            startDate: start ? moment(start) : null,
            endDate: end ? moment(end) : null,
          });
        }}
        filterDate={this.isSelectable}
        dateFormat="d MMM yyyy"
        placeholderText="Select date range"
        className="no-border w-100 text-center"
      />
    );
  }
}

RangePickerforFrequency.propTypes = {
  startDate: PropTypes.any,
  endDate: PropTypes.any,
  monthsToDisplay: PropTypes.number,
  minimumNights: PropTypes.number,
  onDatesChange: PropTypes.func,
};


export class SingleDateTimePicker extends React.Component {
  valid = (current) => {
    const today = moment(); // always current
    if (this.props.depot) {
      const limit = moment().subtract(7, "days");
      return current.isAfter(limit) && current.isBefore(today);
    } else if (this.props.id === "endTime") {
      const minDate = moment(this.props.minDate).subtract(1, "days");
      return current.isAfter(minDate) && current.isBefore(today);
    } else if (this.props.createVehicle) {
      return false;
    } else {
      return current.isBefore(moment(this.props.date));
    }
  };
  render() {
    // Always pass a Moment object to value
    let inputDate = this.props.date ? moment(this.props.date) : null;
    return (
      <DateTime
        isValidDate={this.valid}
        inputProps={{
          placeholder: this.props.placeholder || "Select Date",
          readOnly: !(this.props.depot), // true if not depot
        }}
        value={inputDate}
        onChange={(date) => {
          this.setState?.({ date }); // Only if you need local state!
          this.props.onDateChange(date);
        }}
      />
    );
  }
}

SingleDateTimePicker.propTypes = {
  date: PropTypes.any,
  onDateChange: PropTypes.func,
  placeholder: PropTypes.string,
  depot: PropTypes.bool,
  id: PropTypes.string,
  minDate: PropTypes.any,
  createVehicle: PropTypes.bool,
};

